import React, { useEffect, useRef } from "react";
import { classNames } from "Utils/utils";
import { CARE_WATCH_VIEWS, QUEUE_TABS } from "../utils/constants";
import { IconAlertTriangle, IconFileText } from "./CareWatchIcons";

const queueTone = (view, count) => {
  const total = Number(count);
  if (view === CARE_WATCH_VIEWS.INCIDENT) {
    if (Number.isNaN(total) || total <= 0) return "is-clear";
    if (total > 1) return "is-alert";
    return "";
  }
  if (view === CARE_WATCH_VIEWS.DOCUMENTATION) {
    if (Number.isNaN(total) || total <= 1) return "is-clear";
    return "is-amber";
  }
  if (view === CARE_WATCH_VIEWS.LIVE) {
    if (Number.isNaN(total) || total <= 0) return "is-clear";
    if (total > 1) return "is-blue";
    return "";
  }
  return "";
};

/** Brief wiggle when the tab total changes (web + responsive web only). */
const CareWatchTabCount = ({ count, tone = "" }) => {
  const nodeRef = useRef(null);
  const prevCount = useRef(undefined);

  useEffect(() => {
    if (count == null || count === "") return;
    const next = Number(count);
    if (Number.isNaN(next)) return;

    const el = nodeRef.current;
    if (!el) {
      prevCount.current = next;
      return;
    }

    if (prevCount.current !== undefined && prevCount.current !== next) {
      el.classList.remove("is-tickle");
      void el.offsetWidth;
      el.classList.add("is-tickle");
    }
    prevCount.current = next;
  }, [count]);

  if (count == null || count === "") return null;
  const displayCount = Number(count);
  if (Number.isNaN(displayCount)) return null;

  return (
    <span
      ref={nodeRef}
      className={classNames("care-watch-tab-count", tone)}
      onAnimationEnd={(event) => {
        const name = event.animationName || "";
        if (name.includes("care-watch-tab-count-tickle")) {
          event.currentTarget.classList.remove("is-tickle");
        }
      }}
    >
      {displayCount}
    </span>
  );
};

const TAB_ICONS = {
  "alert-triangle": IconAlertTriangle,
  "file-text": IconFileText,
};

const LivePulse = () => (
  <span className="care-watch-live-pulse" aria-hidden="true">
    <span className="care-watch-live-ring care-watch-live-ring-outer" />
    <span className="care-watch-live-ring care-watch-live-ring-mid" />
    <span className="care-watch-live-dot" />
  </span>
);

const CareWatchQueueTabs = ({ value, onChange, counts = {} }) => {
  const selectedIndex = Math.max(
    0,
    QUEUE_TABS.findIndex((tab) => tab.value === value)
  );

  return (
    <div className="care-watch-tabs" role="tablist" aria-label="Care Watch queues">
      <div
        className="care-watch-tabs-well"
        style={{ "--cw-tab-index": selectedIndex }}
      >
        <span className="care-watch-tab-pill" aria-hidden />
        {QUEUE_TABS.map((tab) => {
          const selected = tab.value === value;
          const Icon = TAB_ICONS[tab.icon];
          const count = counts[tab.value];
          const tone = queueTone(tab.value, count);
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={selected}
              className={classNames("care-watch-tab", selected && "is-selected")}
              onClick={() => {
                if (tab.value !== value) onChange(tab.value);
              }}
            >
              <span className="care-watch-tab-copy">
                <span className={classNames("care-watch-tab-icon", tone)}>
                  {tab.icon === "live" ? (
                    <LivePulse />
                  ) : Icon ? (
                    <Icon size={14} stroke="currentColor" />
                  ) : null}
                </span>
                <span className="care-watch-tab-label">{tab.label}</span>
                {typeof count === "number" ? (
                  <CareWatchTabCount count={count} tone={tone} />
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CareWatchQueueTabs;
